<template>
    <div class="home">
        <div class="page-main">
            <div class="container">
                <h2 class="home-title">{{ $t("homeTitle") }}</h2>
                <p>{{ $t("homeSubTitle") }}</p>
            </div>

            <accordion :title="$t('solarRadiationCalculation')">
                <form>
                    <div class="form-group">
                        <label for="greenhouseTransmission">
                            <strong>{{ $t("greenhouseTransmission") }}</strong>
                            {{ $t("greenhouseTransmissionUnit") }}
                            <span
                                    class="tooltip-indicator"
                                    v-tooltip.left="$t('greenhouseTransmissionToolTip')"
                            ></span>
                        </label>

                        <div class="slider-with-input-container">
                            <div class="slider">
                                <vue-slider v-model="greenhouseTransmission"></vue-slider>
                            </div>

                            <input
                                    class="input form-control"
                                    type="number"
                                    v-model="greenhouseTransmission"
                            />
                        </div>
                    </div>

                    <div class="form-group linked-top">
                        <label for="intensityOutside">
                            <strong>{{ $t("intensityOutside") }}</strong>
                            {{ $t("intensityOutsideUnit") }}
                            <span
                                    class="tooltip-indicator"
                                    v-tooltip.left="$t('intensityOutsideToolTip')"
                            ></span>
                        </label>
                        <clearable-input
                                :id="'intensityOutside'"
                                v-model="intensityOutsideProxy"
                                @focus="cleanInput('intensityOutside')"
                                :class="{ error: !isValidNumberInput(intensityOutsideProxy) }"
                        ></clearable-input>
                    </div>

                    <div class="linked-indicator"></div>

                    <div class="form-group linked-bottom">
                        <label for="prInside">
                            <strong>{{ $t("prInside") }}</strong>
                            {{ $t("prInsideUnit") }}
                            <span
                                    class="tooltip-indicator"
                                    v-tooltip.left="$t('prInsideToolTip')"
                            ></span>
                        </label>
                        <clearable-input
                                :id="'prInside'"
                                v-model="prInsideProxy"
                                :class="{ error: !isValidNumberInput(prInsideProxy) }"
                        ></clearable-input>
                    </div>

                    <div class="form-group linked-top">
                        <label for="radiationSUM">
                            <strong>{{ $t("radiationSUM") }}</strong>
                            {{ $t("radiationSUMUnit") }}
                            <span
                                    class="tooltip-indicator"
                                    v-tooltip.left="$t('radiationSUMToolTip')"
                            ></span>
                        </label>
                        <clearable-input
                                :id="'radiationSUM'"
                                v-model="radiationSUMProxy"
                                :class="{ error: !isValidNumberInput(radiationSUMProxy) }"
                        ></clearable-input>
                    </div>

                    <div class="linked-indicator"></div>

                    <div class="form-group linked-bottom">
                        <label for="lightSUM">
                            <strong>{{ $t("lightSUM") }}</strong>
                            {{ $t("lightSUMUnit") }}
                            <span
                                    class="tooltip-indicator"
                                    v-tooltip.left="$t('lightSUMToolTip')"
                            ></span>
                        </label>

                        <clearable-input
                                :id="'lightSUM'"
                                v-model="lightSUMProxy"
                                :class="{ error: !isValidNumberInput(lightSUMProxy) }"
                        ></clearable-input>
                    </div>
                </form>
            </accordion>

            <accordion :title="$t('luxToPar')" :titleStr="'luxToPar'">
                <form>
                    <div class="form-group">
                        <label for="LUX">
                            <strong>{{ $t("LUX") }}</strong>
                            <span
                                    class="tooltip-indicator"
                                    v-tooltip.left="$t('LUXToolTip')"
                            ></span>
                        </label>

                        <clearable-input :id="'LUX'" v-model="LUXProxy"></clearable-input>
                    </div>
                    <div class="form-group">
                        <label for="PAR">
                            <strong>{{ $t("PAR") }}</strong>
                            {{ $t("PARUnit") }}
                            <span
                                    class="tooltip-indicator"
                                    v-tooltip.left="$t('PARToolTip')"
                            ></span>
                        </label>
                        <clearable-input :id="'PAR'" v-model="PARProxy"></clearable-input>
                    </div>
                </form>
            </accordion>

            <accordion :title="$t('ali')">
                <form>
                    <div class="form-group">
                        <label for="lightIntensityMicro">
                            <strong>{{ $t("aliMaxLighting") }}</strong>
                            {{ $t("aliMaxLightingUnit") }}
                            <span
                                    class="tooltip-indicator"
                                    v-tooltip.left="$t('aliMaxLightingToolTip')"
                            ></span>
                        </label>
                        <clearable-input
                                :id="'aliMaxLighting'"
                                v-model="aliMaxLighting"
                        ></clearable-input>
                    </div>

                    <div class="form-row">
                        <div class="form-group col-6">
                            <label for="lightIntensityHPSLED">
                                <strong>{{ $t("lightIntensityHPSLED") }}</strong>
                                <!-- {{ $t("lightIntensityHPSLEDUnit") }} -->
                                <span
                                        class="tooltip-indicator"
                                        v-tooltip.bottom="$t('lightIntensityHPSLEDToolTip')"
                                ></span>
                            </label>
                            <clearable-input
                                    :id="'lightIntensityHPSLED'"
                                    v-model="lightIntensityHPSLED"
                            ></clearable-input>
                        </div>

                    <div class="form-group col-6">
                        <label for="lightIntensityHPSLEDUnit">
                            <strong>{{ $t("unit") }}</strong>
                        </label>
                        <select class="form-control"
                                id="lightIntensityHPSLEDUnit"
                                v-model="selectedUnit"
                        >
                            <option>LUX</option>
                            <option>μmol/m²/s</option>
                        </select>
                    </div>
                </div>

                    <div class="form-group">
                        <label for="lightIntensitySUM">
                            <strong>{{ $t("lightIntensitySUM") }}</strong>
                            {{ $t("lightIntensitySUMUnit") }}

                            <span
                                    class="tooltip-indicator"
                                    v-tooltip.left="$t('lightIntensitySUMToolTip')"
                            ></span>
                        </label>

                        <input
                                :id="'lightIntensitySUM'"
                                class="form-control"
                                v-model="lightIntensitySUM"
                                disabled
                        />
                    </div>
                </form>
            </accordion>

            <accordion :title="$t('requiredSuplementalLightLevel')">
                <form>
                    <div class="form-group">
                        <label for="targetDaily">
                            <strong>{{ $t("targetDaily") }}</strong>
                            {{ $t("targetDailyUnit") }}
                            <span
                                    class="tooltip-indicator"
                                    v-tooltip.left="$t('targetDailyToolTip')"
                            ></span>
                        </label>

                        <clearable-input
                                :id="'targetDaily'"
                                v-model="targetDaily"
                        ></clearable-input>
                    </div>
                    <div class="form-group">
                        <label for="supplementMaxLight">
                            <strong>{{ $t("supplementMaxLight") }}</strong>
                            {{ $t("supplementMaxLightUnit") }}
                            <span
                                    class="tooltip-indicator"
                                    v-tooltip.left="$t('supplementMaxLightToolTip')"
                            ></span>
                        </label>
                        <clearable-input
                                :id="'supplementMaxLight'"
                                v-model="supplementMaxLight"
                        ></clearable-input>
                    </div>
                    <div class="form-group">
                        <label for="naturelLight">
                            <strong>{{ $t("naturelLight") }}</strong>
                            {{ $t("naturelLightUnit") }}
                            <span
                                    class="tooltip-indicator"
                                    v-tooltip.left="$t('naturelLightToolTip')"
                            ></span>
                        </label>
                        <clearable-input
                                :id="'naturelLight'"
                                v-model="naturelLight"
                        ></clearable-input>
                    </div>
                    <div class="form-group">
                        <label for="supplementLight">
                            <strong>{{ $t("supplementLight") }}</strong>
                            {{ $t("supplementLightUnit") }}
                            <span
                                    class="tooltip-indicator"
                                    v-tooltip.left="$t('supplementLightToolTip')"
                            ></span>
                        </label>

                        <input
                                type="number"
                                disabled
                                id="supplementLight"
                                class="form-control"
                                v-model="supplementLight"
                        />
                        <!-- <clearable-input :id="'supplementLight'" disabled v-model="supplementLight"></clearable-input> -->
                    </div>
                </form>
            </accordion>
            <Modal :visible="isModalOpen" @close="isModalOpen = false" :exportData="dataToObject"/>
            <FloatingActionButton
              @click="toggleModal"
              @close="isModalOpen = false"
            />
        </div>
        <Footer></Footer>
    </div>
</template>

<script>
    import Accordion from "@/components/Accordion";
    import Modal from "@/components/Modal.vue";
    import Footer from "@/components/Footer.vue";
    import ClearableInput from "@/components/ClearableInput.vue";
    import closeIcon from "@/assets/icons/horti_07-cross-12px.svg?inline";
    import fabIcon from "@/assets/icons/share_export_icon_horti.svg?inline";
    import { debounce } from 'lodash';
    import FloatingActionButton from "@/components/FloatingActionButton";

    import VueSlider from "vue-slider-component";
    import "vue-slider-component/theme/antd.css";

    const shortcuts = {
        greenhouseTransmission: "ght",
        intensityOutside: "io",
        prInside: "pri",
        radiationSUM: "rs",
        lightSUM: "ls",
        LUX: "lux",
        PAR: "par",
        aliMaxLighting: "aliml",
        lightIntensityMicro: "lim",
        lightIntensityHPSLED: "liml",
        targetDaily: "td",
        supplementMaxLight: "lml",
        naturelLight: "nl"
    };

    const formattedDigitNumber = 2;
    const timeoutBeforeCalculation = 300;

    export default {
        name: "home",
        components: {
            Accordion,
            Modal,
            closeIcon,
            ClearableInput,
            fabIcon,
            VueSlider,
            Footer,
            FloatingActionButton,
        },
        data() {
            return {
                selectedUnit: 'LUX',
                greenhouseTransmission: 70,
                intensityOutside: 0, //200,
                prInside: 0, //301,
                radiationSUM: 0, //500,
                lightSUM: 0, //25,
                LUX: 0, //20000,
                PAR: 0, //130,
                aliMaxLighting: 0,
                lightIntensityMicro: 0,
                lightIntensityHPSLED: 0,
                targetDaily: 0,
                supplementMaxLight: 0,
                naturelLight: 0,
                isModalOpen: false
            };
        },
        mounted() {
            this.setDataFromUrl();
        },
        methods: {
            getFullKeyFromShortKey(shortKey) {
                return Object.keys(shortcuts).find(fullKey => {
                    return shortcuts[fullKey] === shortKey;
                });
            },
            setDataFromUrl() {
                Object.values(shortcuts).map(shortKey => {
                    if (this.$route.query.hasOwnProperty(shortKey)) {
                        const fullKey = this.getFullKeyFromShortKey(shortKey);
                        this[fullKey] = this.$route.query[shortKey];
                    }
                });
            },
            clearInput(fieldname) {
                this[fieldname] = null;
            },
            toggleModal() {
                this.isModalOpen = !this.isModalOpen;
            },
            isValidNumberInput(number) {
                if (number === null || number === "") {
                    return true;
                }
                return !isNaN(number);
            },
            setSourceValues(newValue, sourceProperty, relatedProperty, conversion) {
                if (newValue === "" || newValue === null) {
                    this[sourceProperty] = null;
                    this[relatedProperty] = null;
                } else if (isNaN(Number(newValue))) {
                    this[sourceProperty] = newValue;
                    this[relatedProperty] = newValue;
                } else {
                    this[sourceProperty] = Number(newValue);
                    this[relatedProperty] = conversion(Number(newValue));
                }
            },
            getFormattedResult(value) {
                return Math.round(value * this.getFormattedMultiplier) / this.getFormattedMultiplier;
            }
        },
        watch: {
            greenhouseTransmission() {
                this.intensityOutsideProxy = this.intensityOutsideProxy;
                this.radiationSUMProxy = this.radiationSUMProxy;
            },
            urlData(newValue) {
                this.$router
                    .push({
                        query: {
                            ...this.$route.query,
                            ...newValue
                        }
                    })
                    .catch(err => {
                    }); //vue router throws an error if you navigate to the same page. catch it and do nothing with it since its not a problem for us.
            }
        },
        computed: {
            getFormattedMultiplier: {
                get() {
                    return Math.pow(10, formattedDigitNumber);
                }
            },
            urlData() {
                return Object.keys(shortcuts).reduce((acc, curr) => {
                    acc[shortcuts[curr]] = this[curr];
                    return acc;
                }, {});
            },
            dataToObject() {
                //Get all Data
                const data = this.$data;

                //Map the data to an array
                let allData = [
                    Object.keys(data),
                    Object.values(data)
                ];
                let exportDataHeaders = [];
                let exportDataValues = [];

                //Filter out the unwanted properties, e.g. isModalOpen
                for (let i = 0; i < allData[0].length; i++) {
                    if (shortcuts.hasOwnProperty(allData[0][i])) {
                        exportDataHeaders.push(allData[0][i]);
                        exportDataValues.push(allData[1][i]);
                    }
                }

                //Return the filtered array
                return [
                    exportDataHeaders,
                    exportDataValues
                ]
            },
            decodedUrlData() {
                return Object.keys(shortcuts).reduce((acc, fullKey) => {
                    if (this.$route.query.hasOwnProperty(shortcuts[fullKey])) {
                        acc[fullKey] = this.$route.query[shortcuts[fullKey]];
                    }
                    return acc;
                }, {});
            },
            intensityOutsideProxy: {
                get() {
                    return this.getFormattedResult(this.intensityOutside);
                },
                set: debounce(function(newValue) {
                    this.setSourceValues(
                        newValue,
                        "intensityOutside",
                        "prInside",
                        num => num * (this.greenhouseTransmission * 0.01) * 2.15
                    );
                }, timeoutBeforeCalculation)
            },
            prInsideProxy: {
                get() {
                    return this.getFormattedResult(this.prInside);
                },
                set: debounce(function(newValue) {
                    this.setSourceValues(
                        newValue,
                        "prInside",
                        "intensityOutside",
                        num => num / (this.greenhouseTransmission * 0.01) / 2.15
                    );
                }, timeoutBeforeCalculation)
            },

            radiationSUMProxy: {
                get() {
                    return this.getFormattedResult(this.radiationSUM);
                },
                set: debounce(function(newValue) {
                    this.setSourceValues(
                        newValue,
                        "radiationSUM",
                        "lightSUM",
                        num => (num * (this.greenhouseTransmission * 0.01) * 2.15) / 100
                    );
                }, timeoutBeforeCalculation)
            },

            lightSUMProxy: {
                get() {
                    return this.getFormattedResult(this.lightSUM);
                },
                set: debounce(function(newValue) {
                    this.setSourceValues(
                        newValue,
                        "lightSUM",
                        "radiationSUM",
                        num => (100 * num) / (2.15 * (this.greenhouseTransmission * 0.01))
                    );
                }, timeoutBeforeCalculation)
            },

            LUXProxy: {
                get() {
                    return this.getFormattedResult(this.LUX);
                },
                set: debounce(function(newValue) {
                    this.setSourceValues(newValue, "LUX", "PAR", num => num * 0.013);
                }, timeoutBeforeCalculation)
            },

            PARProxy: {
                get() {
                    return this.getFormattedResult(this.PAR);
                },
                set: debounce(function(newValue) {
                    this.setSourceValues(newValue, "PAR", "LUX", num => num / 0.013);
                }, timeoutBeforeCalculation)
            },

            lightIntensitySUM() {
                let result = 0;
                if (this.selectedUnit === 'LUX') {
                    result = ((this.lightIntensityHPSLED * 0.013) * 3600 * this.aliMaxLighting) / 1000000;
                } else {
                    result = (this.lightIntensityHPSLED * 3600 * this.aliMaxLighting) / 1000000;
                }
                return this.getFormattedResult(result);
            },
            supplementLight() {
                const result = ((this.targetDaily - this.naturelLight) * 1000000) / (this.supplementMaxLight * 3600);
                const calculationResult = this.getFormattedResult(result);
                return Math.max(calculationResult, 0);
            }
        }
    };
</script>

<style lang="scss">
    .home {
        display: flex;
        flex-direction: column;
    }

    .page-main {
        flex: 1 0 auto;
    }

    .form-control {
        &:focus {
            border: 1px solid $dark-grey;
        }
    }
    .disclaimer{
        padding-top: 20px;
        padding-bottom: 10px;
        clear: both;

        p{
            font-style: italic;
            font-size: 14px;

            a{
                color: #0000EE;
            }
        }
    }
    .home-title{
        padding-top: 25px;
        padding-bottom:5px;
        font-size:17px;
        font-weight: 700;
        margin:0;
        position:relative;
        padding-right:50px;
    }
</style>
